/**
 * The Vipupisteet side tab (DrawerSystem), the same place v1 keeps its list:
 * every candidate in rank order, its direction and counts, and the run it
 * came from. Clicking a row selects the pivot and brings its card into view.
 */
import React from 'react';
import styled from '@emotion/styled';
import { DIRECTION } from './PivotCard';
import { useTranslation } from '../hooks/useTranslation.jsx';

const Wrap = styled.div`display: flex; flex-direction: column; gap: 10px; padding: 4px 0;`;
const Stamp = styled.div`font-size: 0.78rem; color: #9ca3af; padding: 0 4px 6px; border-bottom: 1px solid rgba(255,255,255,0.08);`;
const Counts = styled.div`display: flex; gap: 12px; font-size: 0.76rem; color: #9ca3af; padding: 0 4px; b { color: #e8e8ef; font-weight: 600; }`;
const List = styled.ul`list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px;`;
const Row = styled.li`
  display: grid; grid-template-columns: 1.6em 10px 1fr; gap: 8px; align-items: start;
  padding: 8px 8px; border-radius: 8px; cursor: pointer;
  background: ${(p) => (p.$selected ? 'rgba(124,58,237,0.18)' : 'transparent')};
  border: 1px solid ${(p) => (p.$selected ? 'rgba(167,139,250,0.6)' : 'transparent')};
  opacity: ${(p) => (p.$status === 'dismissed' ? 0.45 : 1)};
  &:hover { background: rgba(255,255,255,0.05); }
  &:focus-visible { outline: 1px solid #a78bfa; }
`;
const Rank = styled.span`font-family: ui-monospace, monospace; font-size: 0.72rem; color: #8a8fa3; padding-top: 2px;`;
const Dot = styled.span`width: 10px; height: 10px; border-radius: 50%; margin-top: 4px; background: ${(p) => p.$c};`;
const Title = styled.div`font-size: 0.86rem; line-height: 1.3; color: #e8e8ef;`;
const Sub = styled.div`font-size: 0.72rem; color: #9ca3af; margin-top: 2px;`;

export default function PivotDrawerContent({ candidates = [], run = null, when = null, selectedId = null, onSelect }) {
  const { t } = useTranslation();
  const accepted = candidates.filter((c) => c.status === 'accepted').length;
  const proposed = candidates.filter((c) => c.status === 'proposed').length;
  return (
    <Wrap>
      <Stamp>{run ? t('pivotsV2.drawer.lastRun', { when, count: candidates.length }) : t('pivotsV2.drawer.noRun')}</Stamp>
      {candidates.length > 0 && (
        <Counts><span><b>{accepted}</b> {t('pivotsV2.drawer.accepted')}</span><span><b>{proposed}</b> {t('pivotsV2.drawer.proposed')}</span><span><b>{candidates.length - accepted - proposed}</b> {t('pivotsV2.drawer.dismissed')}</span></Counts>
      )}
      <List>
        {candidates.map((c) => {
          const d = DIRECTION[c.direction] || DIRECTION.contested;
          const conv = c.convergence || {};
          return (
            <Row key={c.id} $selected={c.id === selectedId} $status={c.status} tabIndex={0} role="button"
              onClick={() => onSelect?.(c.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect?.(c.id); } }}>
              <Rank>{c.rank}</Rank>
              <Dot $c={d.color} title={t(`pivotsV2.direction.${d.key}`)} />
              <div>
                <Title>{c.title}</Title>
                <Sub>{t('pivotsV2.drawer.sub', { scenarios: conv.scenarios, participants: conv.participants })}{c.lane === 'rakenteellinen' ? t('pivotsV2.drawer.structural') : ''}{c.status === 'accepted' ? t('pivotsV2.drawer.subAccepted') : c.status === 'dismissed' ? t('pivotsV2.drawer.subDismissed') : ''}</Sub>
              </div>
            </Row>
          );
        })}
      </List>
    </Wrap>
  );
}
